import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, CircularProgress, useTheme, useMediaQuery, Typography, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { refreshS3Url, handlePdfLoadError, isPdfUrlLikelyExpired } from '../../utils/pdfUtils';

interface LOAPDFViewerProps {
  pdfUrl: string;
  onClose?: () => void;
}

const LOAPDFViewer: React.FC<LOAPDFViewerProps> = ({ pdfUrl }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [height, setHeight] = useState<string>('100%');
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>(pdfUrl);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const objectRef = useRef<HTMLObjectElement>(null);

  // Function to refresh the URL
  const handleRefreshUrl = useCallback(async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Call the API to refresh the URL
      const freshUrl = await refreshS3Url(currentPdfUrl); // Use currentPdfUrl to handle already refreshed URLs
      console.log('Successfully refreshed URL');
      setCurrentPdfUrl(freshUrl);
    } catch (err) {
      console.error('Error refreshing URL:', err);
      setError('Failed to refresh the document. Please try again later.');
    } finally {
      setRefreshing(false);
    }
  }, [currentPdfUrl]);

  // Set a fixed height for mobile to ensure the PDF is visible
  useEffect(() => {
    if (isMobile) {
      // On mobile, set a fixed height that's large enough to show the PDF
      setHeight('70vh');
    } else {
      setHeight('100%');
    }
  }, [isMobile]);

  // Update currentPdfUrl when pdfUrl prop changes
  useEffect(() => {
    setCurrentPdfUrl(pdfUrl);
    setError(null);
    
    // Check if the URL is likely expired and refresh it proactively
    if (isPdfUrlLikelyExpired(pdfUrl)) {
      console.log('URL appears to be expired, refreshing proactively');
      handleRefreshUrl();
    }
  }, [pdfUrl, handleRefreshUrl]);

  // Set loading to false after a short delay
  useEffect(() => {
    // Reset loading state when URL changes
    setLoading(true);
    setError(null);
    
    // Give the PDF a moment to load
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [currentPdfUrl]);
  
  // Function to handle PDF loading errors
  const handlePdfError = () => {
    // Only attempt to refresh if we're not already refreshing and not already showing an error
    if (!refreshing && !error) {
      const errorMessage = isMobile 
        ? 'The PDF could not be displayed on your device. Please use the "Download PDF" button below to open it in your device\'s PDF viewer.'
        : 'The PDF could not be loaded. Attempting to refresh the link...';
      
      setError(errorMessage);
      
      // On desktop, try to auto-refresh. On mobile, just show the error with download option
      if (!isMobile) {
        setRefreshing(true);
        
        // Use the utility function to handle the error and refresh the URL if needed
        handlePdfLoadError(
          currentPdfUrl, // Use currentPdfUrl instead of pdfUrl to handle already refreshed URLs
          (newUrl) => {
            console.log('Successfully refreshed URL automatically');
            setCurrentPdfUrl(newUrl);
            setError(null);
            setRefreshing(false);
          },
          (err) => {
            console.error('Error refreshing URL automatically:', err);
            setError('The PDF could not be loaded. The link may have expired. Please try refreshing manually.');
            setRefreshing(false);
          }
        );
      }
    }
  };

  // Create a URL with parameters that make the PDF more mobile-friendly
  // Using embed instead of view=FitH to ensure content is visible
  // NOTE: Don't add query parameters to presigned URLs - they break the AWS signature!
  // Only add fragment identifiers (after #) which don't affect the signature
  const enhancedPdfUrl = `${currentPdfUrl}#zoom=100`;
  
  console.log('%c PDF Viewer loading URL:', 'background: #222; color: #e67e22', enhancedPdfUrl.substring(0, 150) + '...');
  
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: height,
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        backgroundColor: '#fff'
      }}
    >
      {/* Loading indicator */}
      {loading && !error && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Error message and refresh button */}
      {error && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1000,
            textAlign: 'center',
            width: '80%',
            maxWidth: '400px',
            p: 3,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 3
          }}
        >
          <Typography variant="h6" color="error" gutterBottom>
            Document Error
          </Typography>
          <Typography variant="body1" paragraph>
            {error}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column', alignItems: 'center' }}>
            {isMobile ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => window.open(currentPdfUrl, '_blank')}
                sx={{ mt: 2 }}
              >
                Download PDF
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshUrl}
                disabled={refreshing}
                sx={{ mt: 2 }}
              >
                {refreshing ? 'Refreshing...' : 'Refresh Document'}
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Direct PDF embed with optimized parameters */}
      <object
        ref={objectRef}
        data={enhancedPdfUrl}
        type="application/pdf"
        width="100%"
        height="100%"
        style={{
          display: 'block',
          border: 'none',
          width: '100%',
          height: '100%',
          minHeight: isMobile ? '65vh' : '500px',
        }}
        onError={handlePdfError}
      >
        <embed 
          src={enhancedPdfUrl} 
          type="application/pdf" 
          width="100%" 
          height="100%" 
          style={{
            display: 'block',
            border: 'none',
            width: '100%',
            height: '100%',
          }}
          onError={handlePdfError}
        />
      </object>
    </Box>
  );
};

export default LOAPDFViewer;
